import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "./context/AuthContext";
import LandingPage from "./pages/LandingPage";
import AuthPage from "./pages/AuthPage";
import UploadPage from "./pages/UploadPage";
import ResultsPage from "./pages/ResultsPage";
import PricingPage from "./pages/PricingPage";
import SettingsPage from "./pages/SettingsPage";
import CompanySetupPage from "./pages/CompanySetupPage";
import InvoiceBuilderPage from "./pages/InvoiceBuilderPage";
import InvoicesPage from "./pages/InvoicesPage";
import BuilderInvoiceDetailPage from "./pages/BuilderInvoiceDetailPage";
import InvoiceDetailPage from "./pages/InvoiceDetailPage";
import AnalyticsPage from "./pages/AnalyticsPage";
import ProductMasterPage from "./pages/ProductMasterPage";
import GSTReportsPage from "./pages/GSTReportsPage";
import ReceivablesPage from "./pages/ReceivablesPage";
import CustomersPage from "./pages/CustomersPage";
import ReceivedInvoicesPage from "./pages/ReceivedInvoicesPage";
import FinancialReportsPage from "./pages/FinancialReportsPage";
import MSMECompliancePage from "./pages/MSMECompliancePage";
import RecurringInvoicesPage from "./pages/RecurringInvoicesPage";
import DashboardPage from "./pages/DashboardPage";
import CADashboardPage from "./pages/CADashboardPage";
import CAClientDetailPage from "./pages/CAClientDetailPage";
import ClaimInvoicePage from "./pages/ClaimInvoicePage";
import ProfessionalProfilePage from "./pages/ProfessionalProfilePage";
import ProfessionalSetupPage from "./pages/ProfessionalSetupPage";
import AdminPage from "./pages/AdminPage";
import ForgotPasswordPage from "./pages/ForgotPasswordPage";
import CreditNotesPage from "./pages/CreditNotesPage";
import CreditNoteDetailPage from "./pages/CreditNoteDetailPage";
import QuotationsPage from "./pages/QuotationsPage";
import QuotationBuilderPage from "./pages/QuotationBuilderPage";
import QuotationDetailPage from "./pages/QuotationDetailPage";
import PublicQuotationPage from "./pages/PublicQuotationPage";
import CustomerDetailPage from "./pages/CustomerDetailPage";
import FindCAPage from "./pages/FindCAPage";
import SeoLandingTemplate from "./pages/SeoLandingTemplate";
import SamplePage from "./pages/SamplePage";
import BlogIndexPage from "./pages/BlogIndexPage";
import BlogPostPage from "./pages/BlogPostPage";
import ProfessionLandingPage from "./pages/ProfessionLandingPage";
import CALandingPage from "./pages/CALandingPage";
import SEODashboardPage from "./pages/SEODashboardPage";
import ManageSubscriptionPage from "./pages/ManageSubscriptionPage";
import { SEO_LANDING_PAGES } from "./seo/landingPagesConfig";
import Header from "./components/Header";
import AuthCallback from "./components/AuthCallback";
import GuidedTour from "./components/GuidedTour";
import TrialWelcomeModal from "./components/TrialWelcomeModal";
import PageviewTracker from "./components/PageviewTracker";
import { PlanProvider } from "./context/PlanContext";
import { Loader2 } from "lucide-react";

function ProtectedRoute({ children, requireCompany = false }) {
  const { isAuthenticated, hasCompany, loading, isCA, user } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/auth" state={{ from: location.pathname }} replace />;
  }

  // CA users never need company setup — redirect to CA dashboard or professional setup
  if (isCA && location.pathname === "/company/setup") {
    return <Navigate to="/professional/setup" replace />;
  }

  // Only enforce company requirement for non-CA users
  if (requireCompany && !hasCompany && !isCA && user?.role !== "admin") {
    return <Navigate to="/company/setup" replace />;
  }

  return children;
}

function AppRouter() {
  const { isAuthenticated } = useAuth();
  return (
    <>
      <Header />
      <PageviewTracker />
      {isAuthenticated && <GuidedTour />}
      {isAuthenticated && <TrialWelcomeModal />}
      <Routes>
        {/* Public */}
        <Route path="/" element={<LandingPage />} />
        {/* ── Multi-language mirrors of public marketing pages (Hindi, Marathi, Gujarati, Tamil) ── */}
        {["hi", "mr", "gu", "ta"].flatMap((lng) => [
          <Route key={`lng-${lng}-home`} path={`/${lng}`} element={<LandingPage />} />,
          <Route key={`lng-${lng}-pricing`} path={`/${lng}/pricing`} element={<PricingPage />} />,
          <Route key={`lng-${lng}-for-ca`} path={`/${lng}/for-ca`} element={<CALandingPage />} />,
          <Route key={`lng-${lng}-blog`} path={`/${lng}/blog`} element={<BlogIndexPage />} />,
        ])}
        <Route path="/auth" element={<AuthPage />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/auth/google/callback" element={<AuthCallback />} />
        <Route path="/pricing" element={<PricingPage />} />
        <Route path="/claim/:shareToken" element={<ClaimInvoicePage />} />
        <Route path="/quote/:shareToken" element={<PublicQuotationPage />} />
        <Route path="/pro/:proId" element={<ProfessionalProfilePage />} />

        {/* ── Public SEO landing pages (organic growth) ── */}
        {SEO_LANDING_PAGES.map((p) => (
          <Route key={p.slug} path={`/${p.slug}`} element={<SeoLandingTemplate slug={p.slug} />} />
        ))}
        <Route path="/sample/invoice" element={<SamplePage doc="invoice" />} />
        <Route path="/sample/quotation" element={<SamplePage doc="quotation" />} />

        {/* ── Blog (Learning Center) ── */}
        <Route path="/blog" element={<BlogIndexPage />} />
        <Route path="/blog/:slug" element={<BlogPostPage />} />

        {/* ── Programmatic SEO: profession landing pages ── */}
        <Route path="/invoice-format/:profession" element={<ProfessionLandingPage />} />

        {/* ── CA-specific landing page (B2B channel) ── */}
        <Route path="/for-ca" element={<CALandingPage />} />

        {/* Dashboard — default post-login page */}
        <Route path="/dashboard" element={<ProtectedRoute><DashboardPage /></ProtectedRoute>} />

        {/* Auth required */}
        <Route path="/company/setup" element={<ProtectedRoute><CompanySetupPage /></ProtectedRoute>} />
        <Route path="/professional/setup" element={<ProtectedRoute><ProfessionalSetupPage /></ProtectedRoute>} />
        <Route path="/settings" element={<ProtectedRoute><SettingsPage /></ProtectedRoute>} />
        <Route path="/manage-subscription" element={<ProtectedRoute><ManageSubscriptionPage /></ProtectedRoute>} />

        {/* Public — anyone can start creating an invoice; auth is only asked when they download/share/save */}
        <Route path="/create" element={<InvoiceBuilderPage />} />
        <Route path="/create-invoice" element={<InvoiceBuilderPage />} />
        {/* Auth + Company required */}
        <Route path="/upload" element={<ProtectedRoute requireCompany><UploadPage /></ProtectedRoute>} />
        <Route path="/results" element={<ProtectedRoute requireCompany><ResultsPage /></ProtectedRoute>} />
        <Route path="/invoices" element={<ProtectedRoute requireCompany><InvoicesPage /></ProtectedRoute>} />
        <Route path="/invoices/:invoiceId" element={<ProtectedRoute requireCompany><BuilderInvoiceDetailPage /></ProtectedRoute>} />
        <Route path="/credit-notes" element={<ProtectedRoute requireCompany><CreditNotesPage /></ProtectedRoute>} />
        <Route path="/credit-notes/:creditNoteId" element={<ProtectedRoute requireCompany><CreditNoteDetailPage /></ProtectedRoute>} />
        <Route path="/quotations" element={<ProtectedRoute requireCompany><QuotationsPage /></ProtectedRoute>} />
        <Route path="/quotations/new" element={<ProtectedRoute requireCompany><QuotationBuilderPage /></ProtectedRoute>} />
        <Route path="/quotations/:quotationId" element={<ProtectedRoute requireCompany><QuotationDetailPage /></ProtectedRoute>} />
        <Route path="/find-ca" element={<ProtectedRoute><FindCAPage /></ProtectedRoute>} />
        <Route path="/analytics" element={<ProtectedRoute requireCompany><AnalyticsPage /></ProtectedRoute>} />
        <Route path="/products" element={<ProtectedRoute requireCompany><ProductMasterPage /></ProtectedRoute>} />
        <Route path="/reports" element={<ProtectedRoute requireCompany><GSTReportsPage /></ProtectedRoute>} />
        <Route path="/receivables" element={<ProtectedRoute requireCompany><ReceivablesPage /></ProtectedRoute>} />
        <Route path="/customers" element={<ProtectedRoute requireCompany><CustomersPage /></ProtectedRoute>} />
        <Route path="/customers/:customerId" element={<ProtectedRoute requireCompany><CustomerDetailPage /></ProtectedRoute>} />
        <Route path="/received" element={<ProtectedRoute requireCompany><ReceivedInvoicesPage /></ProtectedRoute>} />
        <Route path="/financial-reports" element={<ProtectedRoute requireCompany><FinancialReportsPage /></ProtectedRoute>} />
        <Route path="/msme" element={<ProtectedRoute requireCompany><MSMECompliancePage /></ProtectedRoute>} />
        <Route path="/recurring" element={<ProtectedRoute requireCompany><RecurringInvoicesPage /></ProtectedRoute>} />
        <Route path="/invoice/:invoiceId" element={<ProtectedRoute requireCompany><InvoiceDetailPage /></ProtectedRoute>} />

        {/* Public — Viral Loop (no auth) */}
        <Route path="/claim/:shareToken" element={<ClaimInvoicePage />} />

        {/* CA Routes — no company required */}
        <Route path="/ca/dashboard" element={<ProtectedRoute><CADashboardPage /></ProtectedRoute>} />
        <Route path="/ca/clients/:clientId" element={<ProtectedRoute><CAClientDetailPage /></ProtectedRoute>} />

        {/* Admin */}
        <Route path="/admin" element={<ProtectedRoute><AdminPage /></ProtectedRoute>} />
        <Route path="/admin/seo" element={<ProtectedRoute><SEODashboardPage /></ProtectedRoute>} />
      </Routes>
    </>
  );
}

function App() {
  return (
    <div className="App min-h-screen bg-[#F9FAFB]">
      <BrowserRouter>
        <AuthProvider>
          <PlanProvider>
            <AppRouter />
          </PlanProvider>
        </AuthProvider>
      </BrowserRouter>
      <Toaster position="top-right" richColors />
    </div>
  );
}

export default App;
